import React from 'react';
import { Typography, Card as AntCard, Button } from 'antd';
import { Cloud, Thermometer, Droplets, Wind, Sun, Gauge, Wifi, Battery } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

// Type workaround for Card component
const Card = AntCard as any;

const { Title, Text, Paragraph } = Typography;

export const WeatherStation: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Helmet>
        <title>Weather Station by QodeAris - Precision Agriculture</title>
        <meta name="description" content="Advanced weather monitoring system for smart farming. Track temperature, humidity, rainfall, and wind speed in real-time." />
        <meta name="keywords" content="weather station, smart farming weather, precision agriculture, rainfall sensor, wind speed monitor" />
      </Helmet>
      <Navbar />
      <main id="main-content" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Title level={1} className="text-5xl md:text-6xl font-bold mb-6">
              Weather Station
              <span className="bg-gradient-to-r from-blue-600 to-sky-600 bg-clip-text text-transparent"> by QodeAris</span>
            </Title>
            <Paragraph className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              Advanced weather monitoring system providing real-time meteorological data 
              for precision agriculture and farm planning.
            </Paragraph>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                type="primary"
                size="large"
                className="bg-blue-600 hover:bg-blue-700"
              >
                Request Information
              </Button>
              <Button
                size="large"
                href="/contact"
              >
                Contact Sales
              </Button>
            </div>
          </motion.div>

          {/* Coming Soon Notice */}
          <div className="mb-16 bg-gradient-to-r from-blue-600 to-sky-600 text-white rounded-2xl p-12 text-center">
            <Cloud className="w-20 h-20 mx-auto mb-6" aria-hidden="true" />
            <Title level={2} className="text-white mb-4">Coming Soon</Title>
            <Paragraph className="text-xl text-white/90 max-w-2xl mx-auto">
              Our advanced Weather Station is currently under development. 
              It will provide comprehensive meteorological data to help farmers make informed decisions.
            </Paragraph>
          </div>

          {/* Planned Features */}
          <section className="mb-16">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Planned Features</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Advanced weather monitoring capabilities for smart farming
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Thermometer className="w-16 h-16 text-red-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Temperature Monitoring</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Accurate temperature readings for crop management
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Droplets className="w-16 h-16 text-blue-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Humidity & Rainfall</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Track humidity levels and rainfall patterns
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Wind className="w-16 h-16 text-cyan-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Wind Monitoring</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Wind speed and direction for spray planning
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Sun className="w-16 h-16 text-yellow-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Solar Radiation</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Measure sunlight intensity for crop growth
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Gauge className="w-16 h-16 text-purple-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Atmospheric Pressure</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Barometric pressure for weather prediction
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Wifi className="w-16 h-16 text-green-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Real-time Data</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Wireless connectivity for instant updates
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Battery className="w-16 h-16 text-orange-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Solar Powered</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    Eco-friendly operation with battery backup
                  </Text>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <Card className="h-full shadow-lg hover:shadow-xl transition-shadow text-center">
                  <Cloud className="w-16 h-16 text-indigo-600 mx-auto mb-4" aria-hidden="true" />
                  <Title level={4} className="mb-2">Weather Forecasting</Title>
                  <Text className="text-gray-600 dark:text-gray-300">
                    AI-powered local weather predictions
                  </Text>
                </Card>
              </motion.div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-16 bg-white dark:bg-gray-800 rounded-2xl p-8 md:p-12">
            <Title level={2} className="text-center mb-8">Benefits for Farmers</Title>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Optimize irrigation based on weather conditions',
                'Plan spraying activities with wind data',
                'Predict frost and take preventive measures',
                'Track growing degree days for crop development',
                'Make data-driven farming decisions',
                'Reduce crop losses from weather events',
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <span className="text-blue-600 text-xl flex-shrink-0">✓</span>
                  <Text className="text-lg text-gray-700 dark:text-gray-300">{benefit}</Text>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <Card className="bg-gradient-to-r from-blue-600 to-sky-600 text-white border-0 shadow-xl">
              <div className="text-center py-8">
                <Title level={2} className="text-white mb-4">Interested in Our Weather Station?</Title>
                <Paragraph className="text-xl text-white/90 max-w-3xl mx-auto mb-6">
                  Register your interest to be notified when the Weather Station becomes available. 
                  Early adopters will receive special pricing and priority installation.
                </Paragraph>
                <div className="flex gap-4 justify-center flex-wrap">
                  <a href="/contact">
                    <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                      Register Interest
                    </button>
                  </a>
                  <a href="/products/dhartinetra">
                    <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors">
                      Explore DhartiNetra
                    </button>
                  </a>
                </div>
              </div>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
